import "./Hero.scss";

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import Section from "../../../components/Section/Section";
import { ALL_PROJECTS } from "../../../config/projects";
import AtomNetwork from "./AtomNetwork/AtomNetwork";

gsap.registerPlugin(ScrollTrigger);

const Hero = () => {
  const { t } = useTranslation();

  const openSourceProjects = t("opensource.projects", {
    returnObjects: true,
  }) as unknown[];
  const totalProjects = ALL_PROJECTS.length + openSourceProjects.length;

  useEffect(() => {
    const heroElement = document.querySelector<HTMLElement>(".hero");
    if (!heroElement) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (prefersReducedMotion) return;

    const context = gsap.context(() => {
      const intro = gsap.timeline({
        defaults: { ease: "none", duration: 0.24 },
      });

      intro
        .fromTo(
          ".hero .heroTag",
          { autoAlpha: 0, y: 10 },
          { autoAlpha: 1, y: 0 },
        )
        .fromTo(
          ".hero h1 .l1",
          { autoAlpha: 0, y: 14 },
          { autoAlpha: 1, y: 0 },
          "-=0.12",
        )
        .fromTo(
          ".hero h1 .l2",
          { autoAlpha: 0, y: 14 },
          { autoAlpha: 1, y: 0 },
          "-=0.18",
        )
        .fromTo(
          ".hero h1 .l3",
          { autoAlpha: 0, y: 14 },
          { autoAlpha: 1, y: 0 },
          "-=0.18",
        )
        .fromTo(
          ".hero .heroDesc",
          { autoAlpha: 0, y: 10 },
          { autoAlpha: 1, y: 0 },
          "-=0.12",
        )
        .fromTo(
          ".hero .heroActions",
          { autoAlpha: 0, y: 10 },
          { autoAlpha: 1, y: 0, duration: 0.2 },
          "-=0.1",
        );

      gsap.fromTo(
        ".hero .heroStats .stat",
        { autoAlpha: 0, x: 10 },
        {
          autoAlpha: 1,
          x: 0,
          duration: 0.2,
          stagger: 0.03,
          ease: "none",
          delay: 0.02,
        },
      );

      gsap.fromTo(
        ".hero .scrollInd",
        { autoAlpha: 0, y: 8 },
        { autoAlpha: 1, y: 0, duration: 0.18, ease: "none", delay: 0.08 },
      );

      gsap.fromTo(
        ".hero .atomNetwork",
        { autoAlpha: 0, scale: 1.02 },
        { autoAlpha: 0.52, scale: 1, duration: 0.2, ease: "none" },
      );

      gsap.to(".hero .orb1", {
        x: 26,
        y: -20,
        duration: 3.6,
        ease: "none",
        repeat: -1,
        yoyo: true,
      });

      gsap.to(".hero .orb2", {
        x: -20,
        y: 18,
        duration: 4.4,
        ease: "none",
        repeat: -1,
        yoyo: true,
      });

      gsap.to(".hero .heroContent", {
        yPercent: -10,
        ease: "none",
        scrollTrigger: {
          trigger: heroElement,
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      });

      gsap.to(".hero .heroStats", {
        yPercent: -18,
        ease: "none",
        scrollTrigger: {
          trigger: heroElement,
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      });

      gsap.to(".hero .atomNetwork", {
        yPercent: 8,
        scale: 1.03,
        ease: "none",
        scrollTrigger: {
          trigger: heroElement,
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      });
    }, heroElement);

    return () => context.revert();
  }, []);

  return (
    <Section id="hero" className="hero">
      <AtomNetwork />
      <div className="orb orb1" />
      <div className="orb orb2" />

      <div className="heroContent">
        <div className="heroTag">{t("hero.tag")}</div>

        <h1>
          <span className="l1">{t("hero.title_l1")}</span>
          <span className="l2">{t("hero.title_l2")}</span>
          <span className="l3">{t("hero.title_l3")}</span>
        </h1>

        <p className="heroDesc">{t("hero.description")}</p>

        <div className="heroActions">
          <a href="#contact" className="btnPrimary">
            {t("hero.cta_primary")}
          </a>
          <a href="#portfolio" className="btnGhost">
            {t("hero.cta_secondary")}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 8h10M9 4l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        </div>
      </div>

      <div className="heroStats">
        <div className="stat">
          <div className="statNum">
            {totalProjects}
            <em>+</em>
          </div>
          <div className="statLabel">{t("hero.stat_projects")}</div>
        </div>
        <div className="stat">
          <div className="statNum">
            5<em>+</em>
          </div>
          <div className="statLabel">{t("hero.stat_blockchain")}</div>
        </div>
        <div className="stat">
          <div className="statNum">2</div>
          <div className="statLabel">{t("hero.stat_founders")}</div>
        </div>
      </div>

      <div className="scrollInd">
        <div className="scrollLine" />
        {t("hero.scroll")}
      </div>
    </Section>
  );
};

export default Hero;

